// 인증·계정 API — 백엔드 kr.taplink.iam.AuthController 와 1:1 대응
// 타입은 OpenAPI 계약에서 생성(DSN-3)
import type { Schemas } from "@taplink/api-client";
import { csrfHeaders, request } from "./client.ts";

export type TermsItem = Schemas["TermsItem"];
export type AgreementInput = Schemas["AgreementInput"];
export type TokenResponse = Schemas["TokenResponse"];

/**
 * status·role은 DB varchar에서 오므로 명세상 `string`이다.
 * 화면에서 분기하려면 좁은 타입이 필요해 교차 타입으로 덧씌운다.
 * ⚠️ 백엔드 값 집합이 바뀌면 여기도 손대야 한다 —
 *    status는 db/migration V4 `ck_account_status_v4`, role은 account.role 이 정본.
 */
export type Me = Omit<Schemas["MeResponse"], "status" | "role"> & {
  status: "PENDING_TERMS" | "ACTIVE" | "SUSPENDED" | "WITHDRAWN" | "DORMANT";
  /** 운영 메뉴 표시 판단용 힌트 — 실제 권한은 서버가 매 요청마다 검증한다 */
  role: "USER" | "SYSTEM_ADMIN";
};

export const authApi = {
  terms: () => request<TermsItem[]>("/auth/terms", { anonymous: true }),

  signup: (loginId: string, password: string, agreements: AgreementInput[]) =>
    request<{ accountId: number; loginId: string; planType: string; status: string }>(
      "/auth/signup",
      { method: "POST", body: { loginId, password, agreements }, anonymous: true },
    ),

  login: (loginId: string, password: string) =>
    request<TokenResponse>("/auth/login", {
      method: "POST",
      body: { loginId, password },
      anonymous: true,
    }),

  me: () => request<Me>("/auth/me"),

  agreeTerms: (agreements: AgreementInput[]) =>
    request<{ termsAgreed: boolean }>("/auth/terms/agree", {
      method: "POST",
      body: { agreements },
    }),

  withdraw: () => request<void>("/auth/me", { method: "DELETE" }),

  /**
   * 로그아웃 — 서버가 리프레시 토큰을 끊고 현재 액세스 토큰을 만료까지 차단한다.
   * 액세스 토큰이 이미 만료됐어도 성공한다(서버가 인증을 요구하지 않는다).
   */
  logout: () => request<void>("/auth/logout", { method: "POST", headers: csrfHeaders() }),

  requestPasswordReset: (loginId: string) =>
    request<{ message: string }>("/auth/password/reset-request", {
      method: "POST",
      body: { loginId },
      anonymous: true,
    }),

  resetPassword: (token: string, newPassword: string) =>
    request<{ message: string }>("/auth/password/reset", {
      method: "POST",
      body: { token, newPassword },
      anonymous: true,
    }),

  verifyEmail: (token: string) =>
    request<{ verified: boolean }>("/auth/email/verify", {
      method: "POST",
      body: { token },
      anonymous: true,
    }),

  resendVerification: () =>
    request<{ message: string }>("/auth/email/verify/resend", { method: "POST" }),
};

export type SocialProvider = "kakao" | "naver" | "google";

/** 소셜 로그인 시작 — 백엔드가 공급자로 리다이렉트한다 */
export function startSocialLogin(provider: SocialProvider): void {
  window.location.href = `/oauth2/authorization/${provider}`;
}

/**
 * 지금 쓸 수 있는 소셜 공급자만 돌려준다.
 *
 * ⚠️ 목록을 서버에서 받아야 한다. 화면에 버튼을 박아 두면 자격증명이 없는 환경에서
 * **눌렀을 때 404** 가 된다(운영 첫날 실측: 카카오·네이버·구글 각 2회).
 * 개발·스테이징도 자격증명이 비어 있는 경우가 많아 같은 문제가 난다.
 *
 * 실패하면 **빈 배열**을 돌려준다 — 목록을 못 읽었을 때 버튼을 보여주는 쪽이
 * 더 나쁜 결과(404)를 만든다. 이메일 로그인은 그대로 동작한다.
 */
export async function fetchSocialProviders(): Promise<SocialProvider[]> {
  try {
    const list = await request<string[]>("/auth/social-providers", { anonymous: true });
    const known: SocialProvider[] = ["kakao", "naver", "google"];
    // 서버가 순서를 정하지만, 모르는 값이 오면 조용히 버린다
    return list.filter((p): p is SocialProvider => (known as string[]).includes(p));
  } catch {
    return [];
  }
}
